import { atom } from 'jotai';
import cloneDeep from 'lodash-es/cloneDeep';
import {
  CategoryFilterItem,
  DEFAULT_SORT_TYPE,
  SearchFilter,
  SearchFilter as SearchFilterItem,
  SORT_TYPE,
} from 'util/search_utils';

export enum FILTER_TYPE {
  COLOR = 'COLOR',
  PRICE = 'PRICE',
  CATEGORY = 'CATEGORY',
  DELIVERY = 'DELIVERY',
}

export interface CategoryFilter {
  type: FILTER_TYPE.CATEGORY;
  value: {
    id: string;
    name: string;
    depth: number;
  };
}

export interface ColorFilter {
  type: FILTER_TYPE.COLOR;
  value: string;
}

export interface PriceFilter {
  type: FILTER_TYPE.PRICE;
  value: {
    min: number;
    max: number;
  };
}

export interface DeliveryFilter {
  type: FILTER_TYPE.DELIVERY;
  value: {
    is_free_shipping: boolean;
  };
}

export type SearchFilterType = CategoryFilter | ColorFilter | PriceFilter | DeliveryFilter;

export const client_search_options_atom = atom<SearchFilterType[]>([]);

export const reset_client_search_options_atom = atom(null, (_, set) => {
  set(client_search_options_atom, []);
});

export const set_free_shipping_atom = atom(null, (_, set, is_active: boolean) => {
  set(client_search_options_atom, (prev) => {
    const next = cloneDeep(prev);
    const has_selected_free_shipping_filter = next.some((item) => item.type === FILTER_TYPE.DELIVERY);

    if (has_selected_free_shipping_filter) {
      return next.filter((p) => p.type !== FILTER_TYPE.DELIVERY);
    }

    return [
      ...next.filter((p) => p.type !== FILTER_TYPE.DELIVERY),
      { type: FILTER_TYPE.DELIVERY, value: { is_free_shipping: is_active } },
    ];
  });
});

export const add_category_atom = atom(null, (_, set, category: CategoryFilterItem) => {
  set(client_search_options_atom, (prev) => [...prev, { type: FILTER_TYPE.CATEGORY, value: category }]);
});

export const remove_category_atom = atom(null, (_, set, category_id: string) => {
  set(client_search_options_atom, (prev) => {
    return prev.filter((p) => {
      return !(p.type === FILTER_TYPE.CATEGORY && category_id === p.value.id);
    });
  });
});

export const set_category_atom = atom(null, (_, set, category_list: CategoryFilterItem[]) => {
  set(client_search_options_atom, (prev) => {
    const search_options_except_category = prev.filter((p) => p.type !== FILTER_TYPE.CATEGORY);
    const new_category_list: SearchFilterType[] = category_list.map((item) => ({
      type: FILTER_TYPE.CATEGORY,
      value: item,
    }));
    return [...search_options_except_category, ...new_category_list];
  });
});

export const add_color_atom = atom(null, (_, set, color: string) => {
  set(client_search_options_atom, (prev) => [...prev, { type: FILTER_TYPE.COLOR, value: color }]);
});

export const remove_color_atom = atom(null, (_, set, color: string) => {
  set(client_search_options_atom, (prev) => {
    return prev.filter((p) => {
      return !(p.type === FILTER_TYPE.COLOR && color === p.value);
    });
  });
});

export const change_price_atom = atom(null, (_, set, { min, max }: { min: number; max: number }) => {
  const has_all_price = min === -1 && max === -1;
  set(client_search_options_atom, (prev) => {
    const next = cloneDeep(prev);
    const has_selected_price_filter = next.some(
      (item) => item.type === FILTER_TYPE.PRICE && min === item.value.min && max === item.value.max,
    );

    if (has_all_price || has_selected_price_filter) {
      return next.filter((p) => p.type !== FILTER_TYPE.PRICE);
    }

    return [...next.filter((p) => p.type !== FILTER_TYPE.PRICE), { type: FILTER_TYPE.PRICE, value: { min, max } }];
  });
});

export const search_filter_atom = atom<SearchFilter>({});

interface SetSearchFilterProps {
  client_search_options: SearchFilterType[];
  init_search_filter: SearchFilter;
  fixed_category_id_list?: string[];
}

export const set_search_filter_atom = atom(
  null,
  (_, set, { init_search_filter, client_search_options, fixed_category_id_list }: SetSearchFilterProps) => {
    set(
      search_filter_atom,
      convertSearchFilter({
        search_filter_list: client_search_options,
        fixed_category_id_list,
        keyword: init_search_filter.keyword,
        is_fast_shipping: init_search_filter.is_fast_shipping,
        is_zigin_shipping: init_search_filter.is_zigin_shipping,
        sort_type: init_search_filter.sort_type ?? DEFAULT_SORT_TYPE,
      }),
    );
  },
);

export const set_client_search_options_atom = atom(null, (get, set, init_search_filter: SearchFilter) => {
  set(client_search_options_atom, getSearchFilterList(init_search_filter));
});

export function getSearchFilterList(init_search_filter: SearchFilterItem): SearchFilterType[] {
  const result: SearchFilterType[] = [];

  if (init_search_filter.category_list) {
    const category_list: CategoryFilter[] = init_search_filter.category_list.map((category) => {
      return {
        type: FILTER_TYPE.CATEGORY,
        value: { ...category },
      };
    });
    result.push(...category_list);
  }

  if (init_search_filter.color_list) {
    const color_list: ColorFilter[] = init_search_filter.color_list.map((color) => ({
      type: FILTER_TYPE.COLOR,
      value: color,
    }));
    result.push(...color_list);
  }

  if (init_search_filter.price) {
    result.push({
      type: FILTER_TYPE.PRICE,
      value: { min: init_search_filter.price.min, max: init_search_filter.price.max },
    });
  }

  if (init_search_filter.is_free_shipping === true) {
    result.push({
      type: FILTER_TYPE.DELIVERY,
      value: { is_free_shipping: init_search_filter.is_free_shipping },
    });
  }

  return result;
}

function convertSearchFilter({
  search_filter_list,
  keyword,
  sort_type,
  is_fast_shipping = false,
  is_zigin_shipping = false,
  fixed_category_id_list,
}: {
  search_filter_list: SearchFilterType[];
  keyword?: string;
  is_zigin_shipping?: boolean;
  is_fast_shipping?: boolean;
  sort_type: SORT_TYPE;
  fixed_category_id_list?: string[];
}): SearchFilter {
  const color_list = search_filter_list.filter((item) => item.type === FILTER_TYPE.COLOR).map((a) => a.value);

  const price = search_filter_list.find((item) => item.type === FILTER_TYPE.PRICE);
  const delivery = search_filter_list.find((item) => item.type === FILTER_TYPE.DELIVERY) as DeliveryFilter | undefined;

  const category_list = fixed_category_id_list
    ? []
    : search_filter_list.filter((item) => item.type === FILTER_TYPE.CATEGORY).map((a) => a.value);

  const category_id_list =
    fixed_category_id_list ??
    // @ts-ignore
    search_filter_list.filter((item) => item.type === FILTER_TYPE.CATEGORY).map((a: CategoryFilter) => a.value.id);

  return {
    keyword,
    sort_type,
    // @ts-ignore
    color_list,
    // @ts-ignore
    category_list,
    category_id_list,
    is_free_shipping: delivery?.value.is_free_shipping ?? false,
    is_zigin_shipping,
    is_fast_shipping,
    price: (price as PriceFilter | undefined)?.value,
  };
}
